import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customerCustomFetch } from "@workspace/api-client-react";
import { AlertTriangle, Loader2, ReceiptText, UtensilsCrossed, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CustomerLoginModal } from "@/components/CustomerLoginModal";
import { useToast } from "@/hooks/use-toast";
import { useBackClose } from "@/hooks/use-back-close";
import { extractApiErrorMessage } from "@/lib/api-error";
import { formatCurrency, orderDisplayCode } from "@/lib/utils";
import { getStoreTimezone } from "@/lib/store-timezone";
import { NomeDaMesa, nomeDaMesa } from "@/lib/mesa";

// Pedido na mesa com o «1 QR code para cada cliente» (Pedro, 29/10/2026): o cardápio da mesa só abre depois do login
// (nome e WhatsApp, o mesmo de hoje), e a mesa que está com outro cliente não abre. «Ver minha conta» vale nos dois
// modos.

function Cartao({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="bg-card max-w-md w-full p-8 rounded-3xl shadow-xl border border-border/50 text-center space-y-4" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

// Só aparece com o «1 QR code para cada cliente», em que o QR é uma comanda.
export function EntrarNaMesa({ mesa, slug, store }: { mesa: string; slug: string; store: Record<string, any> }) {
  const [loginOpen, setLoginOpen] = useState(true);
  return (
    <>
      <Cartao testId="mesa-entrar">
        <UtensilsCrossed className="w-12 h-12 text-primary mx-auto" />
        <div>
          <h2 className="text-2xl font-bold font-display">Comanda {mesa}</h2>
          <p className="text-muted-foreground mt-2">Entre com seu nome e WhatsApp para ver o cardápio.</p>
        </div>
        <Button className="w-full h-12" onClick={() => setLoginOpen(true)} data-testid="mesa-entrar-botao">
          Entrar com WhatsApp
        </Button>
      </Cartao>
      <CustomerLoginModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        title={`Comanda ${mesa}`}
        askBirthDate={store.askBirthDate}
        birthDateRequired={store.birthDateRequired}
        storeSlug={slug}
        loyaltyEnabled={store.loyaltyEnabled === true}
      />
    </>
  );
}

/** «Em qual mesa você está?» (Pedro, 29/10/2026): na primeira leitura da comanda, antes do cardápio. A mesa fica na
 *  comanda e aparece no PDV. */
export function QualMesa({ comanda, onEnviar }: { comanda: string; onEnviar: (mesa: string) => Promise<void> }) {
  const [mesa, setMesa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const enviar = async () => {
    if (!mesa) return;
    setEnviando(true);
    setErro(null);
    try {
      await onEnviar(mesa);
    } catch (err) {
      setErro(extractApiErrorMessage(err, "Não foi possível guardar a mesa. Tente de novo."));
      setEnviando(false);
    }
  };
  return (
    <Cartao testId="mesa-qual-mesa">
      <UtensilsCrossed className="w-12 h-12 text-primary mx-auto" />
      <div>
        <h2 className="text-2xl font-bold font-display">Comanda {comanda}</h2>
        <p className="text-muted-foreground mt-2">Em qual mesa você está?</p>
      </div>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void enviar(); }}>
        <input
          autoFocus
          inputMode="numeric"
          maxLength={3}
          value={mesa}
          onChange={(e) => setMesa(e.target.value.replace(/\D/g, "").slice(0, 3))}
          placeholder="Número da mesa"
          aria-label="Número da mesa"
          className="w-full h-14 rounded-xl border border-input bg-background text-center text-2xl font-bold tracking-widest placeholder:text-base placeholder:font-normal placeholder:tracking-normal"
          data-testid="mesa-qual-mesa-numero"
        />
        {erro && <p className="text-sm text-destructive">{erro}</p>}
        <Button type="submit" className="w-full h-12" disabled={!mesa || enviando} data-testid="mesa-qual-mesa-continuar">
          {enviando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Continuar
        </Button>
      </form>
    </Cartao>
  );
}

export function MesaOcupada() {
  return (
    <Cartao testId="mesa-ocupada">
      <AlertTriangle className="w-12 h-12 text-destructive mx-auto" />
      <p className="text-lg font-bold font-display">
        Este qrcode já está sendo usado por outro cliente.
        <br />
        Procure um funcionário.
      </p>
    </Cartao>
  );
}

interface MinhaConta {
  pedidos: Array<{
    id: string;
    dailyOrderNumber: number | null;
    createdAt: string;
    total: number;
    itens: Array<{ quantidade: number; nome: string; opcoes: string[]; sem: string[]; obs: string | null; subtotal: number }>;
  }>;
  total: number;
}

// «Ver minha conta» (Pedro, 29/10/2026): o botão abre uma gaveta de baixo (90% da tela) com o que o cliente já pediu
// na mesa e ainda está na conta aberta; no fim dela, o «Pedir minha conta» manda o aviso para o app e a impressora.
export function VerMinhaConta({
  slug,
  mesa,
  token,
  codeStyle,
  porCliente = false,
  pedir,
  ocupou,
  compacto = false,
}: {
  slug: string;
  mesa: string;
  token: string;
  codeStyle?: string | null;
  /** Comanda individual: «Comanda 6» no lugar de «Mesa 6». */
  porCliente?: boolean;
  pedir: () => Promise<unknown>;
  ocupou: () => void;
  /** Dentro da faixa da mesa (chave desligada); sem ela, um botão largo no topo. */
  compacto?: boolean;
}) {
  const { toast } = useToast();
  const [aberto, setAberto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [pedida, setPedida] = useState(false);
  const fechar = () => setAberto(false);
  useBackClose(aberto, fechar);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["mesa-minha-conta", slug, mesa],
    queryFn: () =>
      customerCustomFetch<MinhaConta>(`/api/catalog/${slug}/mesa/conta?mesa=${encodeURIComponent(mesa)}&t=${encodeURIComponent(token)}`),
    enabled: aberto,
    staleTime: 0,
  });
  const hora = (iso: string) =>
    new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: getStoreTimezone() });

  const pedirConta = async () => {
    setEnviando(true);
    try {
      await pedir();
      setPedida(true);
    } catch (e) {
      if ((e as { data?: { error?: { code?: string } } })?.data?.error?.code === "MESA_OCUPADA") {
        fechar();
        ocupou();
        return;
      }
      toast({ title: extractApiErrorMessage(e, "Não foi possível pedir a conta. Chame um funcionário."), variant: "destructive" });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={compacto ? "h-8 px-2.5 text-xs shrink-0 bg-background" : "w-full h-11 bg-background border-primary/40"}
        onClick={() => {
          setPedida(false);
          setAberto(true);
        }}
        data-testid="mesa-ver-conta"
      >
        <ReceiptText className={compacto ? "w-3.5 h-3.5 mr-1" : "w-4 h-4 mr-2"} />
        Ver minha conta
      </Button>
      {aberto && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" role="dialog" aria-modal="true" aria-label="Minha conta">
          <div className="absolute inset-0 bg-black/60 animate-in fade-in duration-300" onClick={fechar} />
          <div
            className="relative w-full sm:max-w-md h-[90svh] bg-gray-100 shadow-2xl flex flex-col animate-in slide-in-from-bottom rounded-t-3xl duration-300 overflow-hidden"
            data-testid="mesa-minha-conta"
          >
            <div className="shrink-0 bg-card">
              <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-muted-foreground/25" />
              <div className="flex items-center justify-between p-4 border-b border-border/50">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <ReceiptText className="w-5 h-5 text-secondary" /> Minha conta
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {NomeDaMesa(porCliente)} {mesa}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={fechar} className="rounded-full bg-muted/50" aria-label="Fechar">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
                </div>
              ) : isError || !data ? (
                <div className="py-10 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">Não foi possível carregar a sua conta.</p>
                  <Button variant="outline" onClick={() => void refetch()}>
                    Tentar de novo
                  </Button>
                </div>
              ) : data.pedidos.length === 0 ? (
                <p className="py-12 text-center text-muted-foreground" data-testid="mesa-conta-vazia">
                  Você ainda não fez pedido nesta {nomeDaMesa(porCliente)}.
                </p>
              ) : (
                data.pedidos.map((p) => (
                  <div key={p.id} className="bg-card rounded-2xl border border-border/50 p-4 space-y-2" data-testid="mesa-conta-pedido">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">Pedido #{orderDisplayCode(p, codeStyle)}</span>
                      <span className="text-muted-foreground tabular-nums">{hora(p.createdAt)}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {p.itens.map((i, k) => (
                        <li key={k} className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium">
                              {i.quantidade}x {i.nome}
                            </p>
                            {i.opcoes.length > 0 && <p className="text-xs text-muted-foreground">{i.opcoes.join(", ")}</p>}
                            {i.sem.length > 0 && <p className="text-xs text-muted-foreground">Sem: {i.sem.join(", ")}</p>}
                            {i.obs && <p className="text-xs text-muted-foreground">Obs: {i.obs}</p>}
                          </div>
                          <span className="text-sm font-semibold tabular-nums shrink-0">{formatCurrency(i.subtotal)}</span>
                        </li>
                      ))}
                    </ul>
                    {p.itens.length > 1 && (
                      <div className="flex justify-between border-t border-border/60 pt-2 text-sm">
                        <span className="text-muted-foreground">Total do pedido</span>
                        <span className="font-semibold tabular-nums">{formatCurrency(p.total)}</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="shrink-0 bg-card border-t border-border/50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3">
              {data && data.pedidos.length > 0 && (
                <div className="flex items-center justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="tabular-nums" data-testid="mesa-conta-total">{formatCurrency(data.total)}</span>
                </div>
              )}
              {pedida ? (
                <p className="py-3 text-center font-medium text-green-700" data-testid="mesa-conta-pedida">
                  Pronto, a loja já recebeu o aviso.
                </p>
              ) : (
                <Button className="w-full h-12 text-base" onClick={() => void pedirConta()} disabled={enviando} data-testid="mesa-conta-pedir">
                  {enviando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Pedir minha conta
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
